import React, { useEffect, useState } from 'react';
import { Check, Gift, ShoppingCart, Users, Download, ShieldCheck, ChevronDown, ChevronUp, Shield, Filter, Star, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
function CountdownTimer() {
  const [seconds, setSeconds] = useState(15 * 60);
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(s => s <= 1 ? 15 * 60 : s - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return <div className="bg-red-600 text-white rounded-xl p-4 mb-6 text-center" data-id="element-150">
      <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90" data-id="element-151">
        Últimos minutos para que el descuento esté activo
      </p>
      <div className="flex items-center justify-center gap-2" data-id="element-152">
        <div className="bg-red-800 rounded-lg px-4 py-2 min-w-[56px] text-center" data-id="element-153">
          <span className="text-3xl font-extrabold tabular-nums" data-id="element-154">{mins}</span>
          <p className="text-xs opacity-75 mt-0.5" data-id="element-155">MIN</p>
        </div>
        <span className="text-3xl font-extrabold" data-id="element-156">:</span>
        <div className="bg-red-800 rounded-lg px-4 py-2 min-w-[56px] text-center" data-id="element-157">
          <span className="text-3xl font-extrabold tabular-nums" data-id="element-158">{secs}</span>
          <p className="text-xs opacity-75 mt-0.5" data-id="element-159">SEG</p>
        </div>
      </div>
    </div>;
}
const bonusSummary = [{
  title: 'El Ritual 7×1 (Starter Kit)',
  image: 'https://cdn.magicpatterns.com/uploads/qojd7ppLZJzCA1AcsA5WFN/El-Ritual-71-Starter-Kit.png',
  value: '$890',
  description: 'Todo lo que necesitás para arrancar hoy: guía paso a paso, checklist, tracker de 7 días y tarjeta de heladera con tus 3 reglas diarias.',
  quote: 'Si es fácil, se sostiene. Este kit existe para que no haya excusa el Día 1.'
}, {
  title: 'Guía de Señales Silenciosas',
  image: 'https://cdn.magicpatterns.com/uploads/5FUU7LAuS9NjgnP1JSwzAp/Tu-cuerpo-avisa-en-silencio-antes-de-gritar.png',
  value: '$590',
  description: 'Aprendé a leer las 3 ventanas del día (mañana, post-comida, noche) para saber si lo que sentís es una señal de alerta o solo ruido. Incluye semáforo visual y guion para hablar con tu médico.',
  quote: 'Cuando escuchás temprano, evitás el susto tarde.'
}, {
  title: 'Kit Anti-Picos Express',
  image: 'https://cdn.magicpatterns.com/uploads/mGWupp33G59swixDCTAQmw/Kit-Anti-Picos-Express-Post-Comida-en-10-Min.png',
  value: '$410',
  description: 'Un protocolo de 10 minutos para después de una comida pesada. Tres versiones: casa, trabajo y asado. Sin gimnasio, sin pastillas, sin irse de la mesa.',
  quote: 'No cambies tu comida hoy. Cambiá lo que hacés después.'
}];
const BASE_PRICE = 290;
const BUMP1_PRICE = 397;
const BUMP2_PRICE = 397;
function buildCartUrl(bump1: boolean, bump2: boolean) {
  if (bump1 && bump2) {
    // Both upsells
    return 'https://resetdiario.com/cart/53248593723756:1';
  }
  if (bump1) {
    // Only Escudo Glicémico
    return 'https://resetdiario.com/cart/53248592937324:1';
  }
  if (bump2) {
    // Only Filtros Maestros
    return 'https://resetdiario.com/cart/53248593985900:1';
  }
  // Base product only
  return 'https://www.resetdiario.com/cart/53153601519980:1';
}
export function OfferSection() {
  const [expandedBonus, setExpandedBonus] = useState<number | null>(null);
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const toggleBonus = (idx: number) => {
    setExpandedBonus(expandedBonus === idx ? null : idx);
  };
  const totalPrice = BASE_PRICE + (bump1 ? BUMP1_PRICE : 0) + (bump2 ? BUMP2_PRICE : 0);
  const cartUrl = buildCartUrl(bump1, bump2);
  // Value summary calculations
  const originalProtocol = 580;
  const originalBonuses = 1890;
  const originalBump1 = 790;
  const originalBump2 = 690;
  const totalOriginal = originalProtocol + originalBonuses + (bump1 ? originalBump1 : 0) + (bump2 ? originalBump2 : 0);
  return <section id="offer" className="py-16 px-4 bg-emerald-50" data-id="element-160">
      <style data-id="element-161">{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .bump-shimmer {
          position: relative;
          overflow: hidden;
        }
        .bump-shimmer::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.08) 40%,
            rgba(255,255,255,0.15) 50%,
            rgba(255,255,255,0.08) 60%,
            transparent 100%
          );
          background-size: 200% 100%;
          animation: shimmer 3s ease-in-out infinite;
          pointer-events: none;
          border-radius: inherit;
        }
      `}</style>

      <div className="max-w-5xl mx-auto" data-id="element-162">
        <div className="text-center mb-10" data-id="element-163">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-164">
            Lo Que Recibe Hoy
          </h2>
          <p className="text-xl text-gray-600" data-id="element-165">
            El Protocolo Completo de Limpieza Glicémica de 7 Días
          </p>
        </div>

        <Card variant="highlight" className="max-w-3xl mx-auto relative" data-id="element-166">
          <div className="absolute top-0 right-0 bg-red-600 text-white px-4 py-1 rounded-bl-lg font-bold text-sm uppercase" data-id="element-167">
            50% Descuento
          </div>

          <div className="p-8 md:p-12" data-id="element-168">
            {/* Product */}
            <div className="flex flex-col md:flex-row gap-8 items-center mb-8" data-id="element-169">
              <div className="w-full md:w-1/3" data-id="element-170">
                <img src="https://cdn.magicpatterns.com/uploads/swco9uh88fgzGaxD6EYQFz/Mockup.png" alt="Pack Completo" className="w-full rounded-lg shadow-md" data-id="element-171" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-172">
                <h3 className="text-2xl font-bold text-emerald-800 mb-4" data-id="element-173">
                  El Protocolo de Limpieza Glicémica
                </h3>
                <ul className="space-y-3 mb-6" data-id="element-174">
                  <li className="flex items-start" data-id="element-175">
                    <Check className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-176" />
                    <span className="text-gray-700" data-id="element-177">
                      <strong data-id="element-178">La Guía de las 7 Hierbas Maestras</strong> (valor
                      $1.200)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-179">
                    <Check className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-180" />
                    <span className="text-gray-700" data-id="element-181">
                      <strong data-id="element-182">El Truco del Post-Asado</strong> (valor $500)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-183">
                    <Check className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-184" />
                    <span className="text-gray-700" data-id="element-185">
                      <strong data-id="element-186">La Técnica del Movimiento Invisible</strong>{' '}
                      (valor $400)
                    </span>
                  </li>
                </ul>
                <div className="bg-emerald-100 p-4 rounded-lg flex items-center gap-3" data-id="element-187">
                  <Gift className="h-6 w-6 text-emerald-700" data-id="element-188" />
                  <p className="text-sm text-emerald-800 font-medium" data-id="element-189">
                    Incluye acceso de por vida y actualizaciones futuras gratis.
                  </p>
                </div>
              </div>
            </div>

            {/* Bonuses with expandable details */}
            <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-190">
              <div className="flex items-center gap-2 mb-4" data-id="element-191">
                <Gift className="h-5 w-5 text-emerald-600" data-id="element-192" />
                <h4 className="font-bold text-gray-900" data-id="element-193">
                  + 3 Bonos incluidos GRATIS (valor $1.890 UYU)
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-194">
                {bonusSummary.map((bonus, idx) => <div key={idx} className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col" data-id="element-195">
                    <div className="p-3 flex flex-col items-center text-center" data-id="element-196">
                      <img src={bonus.image} alt={bonus.title} className="w-full h-24 object-cover rounded-md mb-2" data-id="element-197" />
                      <p className="text-xs font-bold text-gray-900 mb-1" data-id="element-198">
                        {bonus.title}
                      </p>
                      <div className="flex items-center gap-1.5 mb-2" data-id="element-199">
                        <span className="text-xs text-gray-400 line-through" data-id="element-200">
                          {bonus.value}
                        </span>
                        <span className="text-xs font-bold text-emerald-700" data-id="element-201">
                          GRATIS
                        </span>
                      </div>
                      <button onClick={() => toggleBonus(idx)} className="flex items-center gap-1 text-emerald-700 hover:text-emerald-800 text-xs font-semibold transition-colors" data-id="element-202">
                        {expandedBonus === idx ? <>
                            Leer menos <ChevronUp className="h-3.5 w-3.5" data-id="element-203" />
                          </> : <>
                            Leer más <ChevronDown className="h-3.5 w-3.5" data-id="element-204" />
                          </>}
                      </button>
                    </div>
                    {expandedBonus === idx && <div className="border-t border-gray-100 bg-gray-50 p-3" data-id="element-205">
                        <p className="text-xs text-gray-700 leading-relaxed mb-2" data-id="element-206">
                          {bonus.description}
                        </p>
                        <div className="bg-emerald-50 text-emerald-800 p-2 rounded text-xs italic border-l-4 border-emerald-500" data-id="element-207">
                          "{bonus.quote}"
                        </div>
                      </div>}
                  </div>)}
              </div>
            </div>

            {/* Price & CTA */}
            <div className="border-t border-gray-200 pt-8" data-id="element-208">
              <CountdownTimer data-id="element-209" />

              <div className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-300 rounded-xl p-3 mb-6" data-id="element-210">
                <Users className="h-5 w-5 text-amber-600 flex-shrink-0" data-id="element-211" />
                <p className="text-amber-800 font-bold text-sm md:text-base" data-id="element-212">
                  Últimos 3 cupos disponibles al precio de oferta
                </p>
              </div>

              {/* Dynamic price */}
              <div className="text-center mb-6" data-id="element-213">
                <p className="text-gray-500 line-through text-xl mb-1" data-id="element-214">
                  Precio Normal: $580 UYU
                </p>
                <div className="flex items-center justify-center gap-3 mb-1" data-id="element-215">
                  <span className="text-5xl md:text-6xl font-extrabold text-gray-900 transition-all duration-300" data-id="element-216">
                    ${totalPrice}
                  </span>
                  <span className="text-2xl font-bold text-gray-600" data-id="element-217">UYU</span>
                  {totalPrice === BASE_PRICE && <span className="bg-red-100 text-red-700 text-sm font-bold px-2 py-1 rounded-lg" data-id="element-218">
                      -50%
                    </span>}
                </div>
                <p className="text-emerald-700 font-semibold text-sm" data-id="element-219">
                  {totalPrice > BASE_PRICE ? `Protocolo $290 + extras seleccionados $${totalPrice - BASE_PRICE}` : 'Precio especial de lanzamiento'}
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 mb-6 text-gray-600 text-sm" data-id="element-220">
                <Download className="h-4 w-4 text-emerald-600" data-id="element-221" />
                <span className="font-semibold text-emerald-700" data-id="element-222">
                  +1.400 descargas
                </span>
                <span className="text-gray-400" data-id="element-223">·</span>
                <span className="font-semibold" data-id="element-224">4.9/5 promedio</span>
              </div>

              <Button size="xl" fullWidth href={cartUrl} trackValue={totalPrice} className="max-w-md mx-auto mb-4 shadow-xl text-lg" data-id="element-225">
                <ShoppingCart className="mr-2 h-6 w-6" data-id="element-226" />
                SÍ, QUIERO MI PROTOCOLO AHORA
              </Button>

              <div className="flex justify-center gap-4 text-xs text-gray-500 mb-6" data-id="element-227">
                <span data-id="element-228">Pago 100% Seguro</span>
                <span data-id="element-229">Descarga Inmediata</span>
                <span data-id="element-230">Envío por Email</span>
              </div>

              {/* ORDER BUMPS */}
              <div className="space-y-4 mb-6" data-id="element-231">
                {/* BUMP 1 — Mantenimiento 365 */}
                <div className={`bump-shimmer rounded-xl border-2 transition-all duration-300 cursor-pointer ${bump1 ? 'border-amber-400 bg-amber-50 shadow-lg shadow-amber-100' : 'border-gray-200 bg-white hover:border-amber-300'}`} onClick={() => setBump1(!bump1)} data-id="element-232">
                  {/* ALTAMENTE RECOMENDADO badge */}
                  <div className="bg-amber-500 text-white text-center py-1.5 px-3 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 rounded-t-[10px]" data-id="element-233">
                    <Star className="h-3.5 w-3.5 fill-current" data-id="element-234" />
                    Altamente Recomendado
                    <Star className="h-3.5 w-3.5 fill-current" data-id="element-235" />
                  </div>
                  <div className="p-4 md:p-5" data-id="element-236">
                    <div className="flex items-start gap-4" data-id="element-237">
                      <div className={`mt-1 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${bump1 ? 'bg-amber-500 border-amber-500' : 'border-gray-300 bg-white'}`} data-id="element-238">
                        {bump1 && <Check className="h-3.5 w-3.5 text-white" data-id="element-239" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-240">
                        <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-241">
                          <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full uppercase" data-id="element-242">
                            Oferta única
                          </span>
                          <span className="text-amber-700 font-extrabold text-sm" data-id="element-243">
                            + ${BUMP1_PRICE} UYU
                          </span>
                        </div>
                        <p className="font-bold text-gray-900 text-sm mb-2" data-id="element-244">
                          No permitas que el esfuerzo de tus 7 días se pierda el
                          próximo domingo...
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3" data-id="element-245">
                          <img src="https://cdn.magicpatterns.com/uploads/fSYWmgeLaaxQjMhDUuKJ57/Mockup_el_escudo_glicemico.png" alt="Mantenimiento 365: El Escudo Glicémico" className="w-full sm:w-28 h-20 object-cover rounded-lg flex-shrink-0" data-id="element-246" />
                          <div className="flex-1" data-id="element-247">
                            <p className="font-bold text-emerald-800 text-xs mb-1.5 flex items-center gap-1.5" data-id="element-248">
                              <Shield className="h-3.5 w-3.5" data-id="element-249" /> Mantenimiento
                              365: El Escudo Glicémico
                            </p>
                            <ul className="text-xs text-gray-600 space-y-1" data-id="element-250">
                              <li className="flex items-start gap-1.5" data-id="element-251">
                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-252" />
                                <span data-id="element-253">
                                  <strong data-id="element-254">Orden de Oro:</strong> Secuenciá tus
                                  comidas para que el azúcar no pase a la sangre
                                </span>
                              </li>
                              <li className="flex items-start gap-1.5" data-id="element-255">
                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-256" />
                                <span data-id="element-257">
                                  <strong data-id="element-258">Truco del Almidón Resistente:</strong>{' '}
                                  Comé arroz, papas y pastas sin subir la
                                  glucosa
                                </span>
                              </li>
                              <li className="flex items-start gap-1.5" data-id="element-259">
                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-260" />
                                <span data-id="element-261">
                                  <strong data-id="element-262">
                                    Protocolo de Salidas Sociales:
                                  </strong>{' '}
                                  Asados y fiestas sin miedo
                                </span>
                              </li>
                              <li className="flex items-start gap-1.5" data-id="element-263">
                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-264" />
                                <span data-id="element-265">
                                  <strong data-id="element-266">Guía de Rescate:</strong> Limpiá un
                                  exceso en menos de 24 horas
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BUMP 2 — Filtros Maestros */}
                <div className={`bump-shimmer rounded-xl border-2 transition-all duration-300 cursor-pointer ${bump2 ? 'border-teal-400 bg-teal-50 shadow-lg shadow-teal-100' : 'border-gray-200 bg-white hover:border-teal-300'}`} onClick={() => setBump2(!bump2)} data-id="element-267">
                  {/* ALTAMENTE RECOMENDADO badge */}
                  <div className="bg-teal-500 text-white text-center py-1.5 px-3 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 rounded-t-[10px]" data-id="element-268">
                    <Star className="h-3.5 w-3.5 fill-current" data-id="element-269" />
                    Altamente Recomendado
                    <Star className="h-3.5 w-3.5 fill-current" data-id="element-270" />
                  </div>
                  <div className="p-4 md:p-5" data-id="element-271">
                    <div className="flex items-start gap-4" data-id="element-272">
                      <div className={`mt-1 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${bump2 ? 'bg-teal-500 border-teal-500' : 'border-gray-300 bg-white'}`} data-id="element-273">
                        {bump2 && <Check className="h-3.5 w-3.5 text-white" data-id="element-274" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-275">
                        <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-276">
                          <span className="bg-teal-100 text-teal-800 text-xs font-bold px-2 py-0.5 rounded-full uppercase" data-id="element-277">
                            Más vendido
                          </span>
                          <span className="text-teal-700 font-extrabold text-sm" data-id="element-278">
                            + ${BUMP2_PRICE} UYU
                          </span>
                        </div>
                        <p className="font-bold text-gray-900 text-sm mb-2" data-id="element-279">
                          ¿Tu azúcar se estancó o baja muy lento? El problema no
                          es tu páncreas... son tus filtros.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3" data-id="element-280">
                          <img src="https://cdn.magicpatterns.com/uploads/mLr68JQ6C9jz9qwuzYCdjx/Filtros_maestros_regeneracion_de_rinones.png" alt="Filtros Maestros: Regeneración de Hígado y Riñones" className="w-full sm:w-28 h-20 object-cover rounded-lg flex-shrink-0" data-id="element-281" />
                          <div className="flex-1" data-id="element-282">
                            <p className="font-bold text-teal-800 text-xs mb-1.5 flex items-center gap-1.5" data-id="element-283">
                              <Filter className="h-3.5 w-3.5" data-id="element-284" /> Filtros
                              Maestros: Hígado y Riñones
                            </p>
                            <ul className="text-xs text-gray-600 space-y-1" data-id="element-285">
                              <li className="flex items-start gap-1.5" data-id="element-286">
                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-287" />
                                <span data-id="element-288">
                                  <strong data-id="element-289">Desgrasado Hepático:</strong> Eliminá
                                  la grasa que rodea tu hígado
                                </span>
                              </li>
                              <li className="flex items-start gap-1.5" data-id="element-290">
                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-291" />
                                <span data-id="element-292">
                                  <strong data-id="element-293">Drenaje Renal:</strong> Limpiá
                                  toxinas, reducí hormigueo y pesadez
                                </span>
                              </li>
                              <li className="flex items-start gap-1.5" data-id="element-294">
                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-295" />
                                <span data-id="element-296">
                                  <strong data-id="element-297">Tónico de la Mañana:</strong> 3
                                  ingredientes en 30 segundos
                                </span>
                              </li>
                              <li className="flex items-start gap-1.5" data-id="element-298">
                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-299" />
                                <span data-id="element-300">
                                  <strong data-id="element-301">Regeneración Celular:</strong> Hierbas
                                  de herboristería para reconstruir filtros
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guarantee */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3 text-left mb-6" data-id="element-302">
                <ShieldCheck className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-303" />
                <div data-id="element-304">
                  <p className="font-bold text-emerald-900 text-sm" data-id="element-305">
                    Garantía de 7 días
                  </p>
                  <p className="text-emerald-800 text-xs mt-1" data-id="element-306">
                    Si no ve resultados, le devolvemos cada peso. Sin preguntas.
                  </p>
                </div>
              </div>

              {/* VALUE SUMMARY TABLE */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-307">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200" data-id="element-308">
                  <p className="font-bold text-gray-900 text-sm text-center" data-id="element-309">
                    Resumen de tu pedido
                  </p>
                </div>
                <div className="divide-y divide-gray-100" data-id="element-310">
                  {/* Base product */}
                  <div className="flex items-center justify-between px-5 py-3" data-id="element-311">
                    <span className="text-sm text-gray-800" data-id="element-312">
                      El Protocolo de Limpieza Glicémica
                    </span>
                    <span className="text-sm text-gray-400 line-through" data-id="element-313">
                      ${originalProtocol}.00
                    </span>
                  </div>
                  {/* Bonuses */}
                  <div className="flex items-center justify-between px-5 py-3" data-id="element-314">
                    <span className="text-sm text-gray-800" data-id="element-315">
                      3 Bonos de Lanzamiento
                    </span>
                    <span className="text-sm text-gray-400 line-through" data-id="element-316">
                      ${originalBonuses}.00
                    </span>
                  </div>
                  {/* Bump 1 (conditional) */}
                  {bump1 && <div className="flex items-center justify-between px-5 py-3 bg-amber-50" data-id="element-317">
                      <span className="text-sm text-gray-800 flex items-center gap-1.5" data-id="element-318">
                        <Shield className="h-3.5 w-3.5 text-amber-600" data-id="element-319" />
                        Mantenimiento 365
                      </span>
                      <span className="text-sm text-gray-400 line-through" data-id="element-320">
                        ${originalBump1}.00
                      </span>
                    </div>}
                  {/* Bump 2 (conditional) */}
                  {bump2 && <div className="flex items-center justify-between px-5 py-3 bg-teal-50" data-id="element-321">
                      <span className="text-sm text-gray-800 flex items-center gap-1.5" data-id="element-322">
                        <Filter className="h-3.5 w-3.5 text-teal-600" data-id="element-323" />
                        Filtros Maestros
                      </span>
                      <span className="text-sm text-gray-400 line-through" data-id="element-324">
                        ${originalBump2}.00
                      </span>
                    </div>}
                </div>
                {/* Totals */}
                <div className="border-t-2 border-gray-200" data-id="element-325">
                  <div className="flex items-center justify-between px-5 py-3" data-id="element-326">
                    <span className="text-sm font-bold text-gray-900" data-id="element-327">
                      Valor Total
                    </span>
                    <span className="text-sm font-bold text-gray-400 line-through" data-id="element-328">
                      ${totalOriginal}.00
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-5 py-4 bg-emerald-600 text-white" data-id="element-329">
                    <span className="font-extrabold text-base" data-id="element-330">
                      Hoy pagás solo
                    </span>
                    <span className="font-extrabold text-2xl transition-all duration-300" data-id="element-331">
                      ${totalPrice}.00
                    </span>
                  </div>
                </div>
              </div>

              {/* SECOND CTA BUTTON */}
              <Button size="xl" fullWidth href={cartUrl} trackValue={totalPrice} className="max-w-md mx-auto mb-3 shadow-xl text-lg" data-id="element-332">
                <ArrowRight className="mr-2 h-6 w-6" data-id="element-333" />
                QUIERO EMPEZAR AHORA
              </Button>
              <p className="text-center text-xs text-gray-500" data-id="element-334">
                Descarga inmediata. Sin esperas. Sin complicaciones.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </section>;
}